import { Badge, Button, Card, Group, Menu, Stack, Text, UnstyledButton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { IconBuildingChurch, IconChevronDown } from "@tabler/icons-react";
import { useEffect } from "react";
import { useParams } from "react-router";
import {
  buildPlatformHost,
  isValidTenantSlug,
  staffDoorUrl,
  type TenantSurface,
} from "@how/tenancy";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "@how/ui";
import type { AppEnv } from "./env";
import { useSupabase } from "./session";
import { useTenant } from "./tenant";

export interface Membership {
  readonly tenantId: string;
  readonly slug: string;
  readonly name: string;
  readonly membershipStatus: "invited" | "active" | "suspended" | "removed";
  readonly tenantStatus: "provisioning" | "active" | "suspended" | "offboarding" | "deleted";
  /** Holds a role other than member, guardian or volunteer in this church (ADR 0065 R11, ADR 0066 R7): the staff dashboard lists only these. */
  readonly hasStaffAccess: boolean;
}

/** Every tenant the caller belongs to, from the my_memberships RPC. */
export function useMyMemberships() {
  const supabase = useSupabase();
  return useQuery({
    queryKey: ["my-memberships"],
    queryFn: async (): Promise<Membership[]> => {
      const { data, error } = await supabase.rpc("my_memberships");
      if (error) throw error;
      return data.map((row) => ({
        tenantId: row.tenant_id,
        slug: row.slug,
        name: row.name,
        membershipStatus: row.membership_status,
        tenantStatus: row.tenant_status,
        hasStaffAccess: row.has_staff_access,
      }));
    },
  });
}

/** The public origin of a tenant surface, e.g. https://alpha.dash.staging.how.dcxlogic.com. */
export function surfaceOrigin(slug: string, surface: TenantSurface, env: AppEnv): string {
  return `https://${buildPlatformHost(slug, surface, env.VITE_HOW_PLATFORM_BASE_DOMAIN)}`;
}

/**
 * Where a church opens. The member portal keeps one address per church. The staff dashboard has one address for all
 * churches (spec section 5): already on the staff door the move stays on this origin (`/<slug>/`, so localhost stays
 * local); from another host it is the door's absolute address once the forward is on, and the church's old dashboard
 * host while it is off (VITE_HOW_STAFF_DOOR_FORWARD, ADR 0065), so nothing points at the door before it exists.
 */
export function churchUrl(
  slug: string,
  surface: TenantSurface,
  env: AppEnv,
  onDoor: boolean,
): string {
  if (surface !== "staff_dashboard") return `${surfaceOrigin(slug, surface, env)}/`;
  if (onDoor) return `/${slug}/`;
  return env.VITE_HOW_STAFF_DOOR_FORWARD
    ? staffDoorUrl(env.VITE_HOW_PLATFORM_BASE_DOMAIN, slug)
    : `${surfaceOrigin(slug, surface, env)}/`;
}

/** A church's HoW member-portal host (`<slug>.members.<base>`) for a hand-off. */
export function memberPortalHost(slug: string, env: AppEnv): string {
  return buildPlatformHost(slug, "member_portal", env.VITE_HOW_PLATFORM_BASE_DOMAIN);
}

/** Whether this page is the staff door: dash.<base> itself, or a church opened under it (`/<slug>`). */
export function useOnStaffDoor(): boolean {
  const tenant = useTenant();
  return (
    (tenant.status === "central" && tenant.app === "staff_door") ||
    (tenant.status === "resolved" && tenant.tenant.basePath !== "")
  );
}

/** The memberships a surface lists: the staff dashboard only those with a staff-side role (ruling R11). */
function forSurface(memberships: readonly Membership[], surface: TenantSurface): Membership[] {
  return memberships.filter((m) => surface !== "staff_dashboard" || m.hasStaffAccess);
}

/**
 * Opening a church: always a full page load, so nothing the app held for the previous church survives. `onDoor` is
 * whether the current page is the staff door (see churchUrl).
 */
export function openChurch(
  slug: string,
  surface: TenantSurface,
  env: AppEnv,
  onDoor = false,
): void {
  window.location.assign(churchUrl(slug, surface, env, onDoor));
}

export interface TenantSwitcherProps {
  readonly env: AppEnv;
  readonly surface: TenantSurface;
}

/** Header control listing the caller's other churches; hidden when there is only one. */
export function TenantSwitcher({ env, surface }: TenantSwitcherProps) {
  const memberships = useMyMemberships();
  const tenant = useTenant();
  const onDoor = useOnStaffDoor();
  const current = tenant.status === "resolved" ? tenant.tenant.slug : null;
  const others = forSurface(memberships.data ?? [], surface).filter(
    (m) => m.slug !== current && m.membershipStatus === "active" && m.tenantStatus === "active",
  );
  if (others.length === 0) return null;
  return (
    <Menu position="bottom-start" width={260}>
      <Menu.Target>
        <UnstyledButton
          aria-label="Switch church"
          style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
        >
          <IconChevronDown size={16} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Switch church</Menu.Label>
        {others.map((m) => (
          <Menu.Item
            key={m.tenantId}
            leftSection={<IconBuildingChurch size={16} />}
            onClick={() => {
              openChurch(m.slug, surface, env, onDoor);
            }}
          >
            {m.name}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

export interface TenantPickerPageProps {
  readonly env: AppEnv;
  readonly surface: TenantSurface;
}

/** The caller's churches (the staff door's picker, or the central host): choose one to open it. */
export function TenantPickerPage({ env, surface }: TenantPickerPageProps) {
  const memberships = useMyMemberships();
  const onDoor = useOnStaffDoor();
  const all = forSurface(memberships.data ?? [], surface);
  const usable = all.filter((m) => m.membershipStatus === "active" && m.tenantStatus === "active");
  // Exactly one listed membership, and it is usable: open it instead of showing a list of one.
  const only = usable.length === 1 && all.length === 1 ? usable[0] : undefined;
  const autoOpen = only !== undefined && surface === "staff_dashboard" ? only : undefined;
  useEffect(() => {
    if (autoOpen !== undefined) {
      window.location.replace(churchUrl(autoOpen.slug, surface, env, onDoor));
    }
  }, [autoOpen, surface, env, onDoor]);
  if (autoOpen !== undefined) return <LoadingState label={`Opening ${autoOpen.name}`} />;
  return (
    <>
      <PageHeader title="Choose a church" description="You belong to the churches below." />
      {memberships.isPending ? (
        <LoadingState rows={3} />
      ) : memberships.isError ? (
        <ErrorState
          message="Could not load your churches."
          onRetry={() => {
            void memberships.refetch();
          }}
        />
      ) : all.length === 0 ? (
        <EmptyState
          title={
            surface === "staff_dashboard"
              ? "You do not have staff access to any church yet"
              : "You are not a member of any church yet"
          }
          description="Ask your church for an invitation, then open the link in that email."
        />
      ) : (
        <Stack gap="sm">
          {all.map((m) => {
            const usable = m.membershipStatus === "active" && m.tenantStatus === "active";
            return (
              <Card key={m.tenantId}>
                <Group justify="space-between" wrap="wrap">
                  <Stack gap={2}>
                    <Text fw={600}>{m.name}</Text>
                    <Group gap="xs">
                      <Text size="sm" c="dimmed">
                        {m.slug}
                      </Text>
                      <StatusBadge
                        domain="membership_status"
                        value={m.membershipStatus}
                        size="sm"
                      />
                      {m.tenantStatus === "active" ? null : (
                        <Badge variant="light" color="gray" size="sm">
                          church {m.tenantStatus}
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                  <Button
                    disabled={!usable}
                    onClick={() => {
                      openChurch(m.slug, surface, env, onDoor);
                    }}
                  >
                    Open
                  </Button>
                </Group>
              </Card>
            );
          })}
        </Stack>
      )}
    </>
  );
}

/** `/t/:slug` on any host: sends the browser to that tenant's origin for this surface. */
export function TenantRedirect({ env, surface }: { env: AppEnv; surface: TenantSurface }) {
  const { slug = "" } = useParams();
  const onDoor = useOnStaffDoor();
  const valid = isValidTenantSlug(slug);
  useEffect(() => {
    if (valid) window.location.replace(churchUrl(slug, surface, env, onDoor));
  }, [valid, slug, surface, env, onDoor]);
  if (!valid) {
    return (
      <ErrorState
        title="That church address is not valid"
        message="Check the link you were given."
      />
    );
  }
  return <LoadingState label="Opening your church" />;
}
